import { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { firebaseAuth } from '@/lib/firebase';
import {
  CANCELLED_POPUP_CODES,
  firebaseErrorCode,
  firebaseErrorMessage,
  isApiError,
} from '@react-apps/auth';
import { extractApiErrorCode } from '@/lib/api-client';
import { authContainer } from '../../di/container';
import { useAuthStore } from '../store/auth.store';
import type {
  FederatedMethod,
  SignInInput,
} from '../../domain/entities/auth.entity';

/** Cópia deste app para `ACCOUNT_NOT_FOUND` — o backend só manda o código. */
const ACCOUNT_NOT_FOUND_MESSAGE =
  'Este email não está cadastrado em nosso sistema';

/**
 * Recusa de senha temporária que chega SEM a temporária em mão (caminho por
 * token, Google, ou recarga com sessão viva). Não dá para reautenticar, então a
 * saída é pedir a entrada por e-mail + temporária, que é a única que resolve.
 */
const PASSWORD_CHANGE_REQUIRED_MESSAGE =
  'Sua senha é temporária e precisa ser trocada. Entre com seu e-mail e a senha temporária para definir uma nova senha.';

/**
 * Códigos em que o fluxo CONTINUA em cima da sessão Firebase recém-criada (o
 * muro de verificação) — nesses, derrubá-la quebraria a própria tela seguinte.
 *
 * `PASSWORD_CHANGE_REQUIRED` **não** entra aqui, e a ausência é carga útil:
 * quando a recusa chega a `handleError` é porque não temos a senha temporária,
 * e manter a sessão nesse caso é o que realimentava a entrada automática da
 * `SignInPage` — ver o comentário do ramo, abaixo.
 */
const KEEPS_SESSION = new Set(['ACCOUNT_NOT_ACTIVATED']);

/**
 * Recusa que significa "não existe conta". No popup federado, o uid acabou de
 * nascer no provedor para um e-mail que não é de ninguém aqui: apagá-lo é o que
 * impede o órfão de existir (D4'), e esta é a única janela em que a sessão ainda
 * é recente o bastante para o `delete()` do Firebase aceitar.
 *
 * Só vale para o popup. No caminho por senha o uid **já existia** antes do
 * clique, e apagá-lo seria destruir credencial alheia por causa de um estado
 * nosso.
 */
const NO_ACCOUNT_CODE = 'ACCOUNT_NOT_FOUND';

/**
 * O uid nasceu NESTE clique? Só então apagá-lo é seguro — e nem o popup basta
 * como prova.
 *
 * Com "vincular contas que usam o mesmo e-mail" (o `allow_duplicate_emails =
 * false`, confirmado em `primum-core-dev` em 2026-09-03), o popup do Google num
 * e-mail que já tinha conta por senha **não** cria uid novo nem falha: entra no
 * uid existente e anexa o provedor. Apagar esse uid destruiria a credencial de
 * senha de alguém por causa de um estado nosso.
 *
 * O uid que o popup acabou de criar tem exatamente um provedor, e é o do
 * popup. É higiene do console, não defesa: quem ataca controla o client, e quem
 * fecha o uid forjado é o provisionamento no back.
 */
const FEDERATED_PROVIDER_IDS = new Set<string>(['google.com', 'microsoft.com']);

function isFreshFederatedUid(user: {
  providerData: { providerId: string }[];
}): boolean {
  return (
    user.providerData.length === 1 &&
    FEDERATED_PROVIDER_IDS.has(user.providerData[0]?.providerId ?? '')
  );
}

/**
 * Microsoft num e-mail que já tem uid (conta por senha, ou nascida de compra sem
 * primeiro acesso): o Firebase não a anexa sozinho, como faz com o Google, e
 * recusa no client antes de criar sessão. Nada a apagar nem a derrubar — a
 * saída é entrar como já entra e vincular no card "Formas de login".
 */
const ACCOUNT_EXISTS_CODE = 'auth/account-exists-with-different-credential';

export function useSignIn() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // E-mail da conta não-verificada → dispara o muro de verificação (§5.1).
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null);
  /** Qual popup está aberto — o botão dele diz "Aguardando…". */
  const [pendingProvider, setPendingProvider] =
    useState<FederatedMethod | null>(null);
  /** O e-mail já tem conta sob outro método: a tela mostra os caminhos. */
  const [accountExists, setAccountExists] = useState(false);
  /**
   * O `onAuthStateChanged` da `SignInPage` dispara no instante em que o Firebase
   * grava a sessão — dentro do popup do Google, ou do
   * `signInWithEmailAndPassword` — ou seja, enquanto o clique já está chamando o
   * backend. Sem esta guarda toda entrada bate **duas vezes** no `sign-in`, e as
   * duas podem terminar diferente (uma entra, a outra derruba a sessão da
   * primeira).
   */
  const entryInFlight = useRef(false);
  const { setAuth } = useAuthStore();
  const navigate = useNavigate();

  const handleSuccess = useCallback(
    (
      data: Awaited<ReturnType<typeof authContainer.repository.signIn>>,
      email: string,
    ) => {
      const accessToken = data.accessToken ?? data.tokens?.accessToken ?? '';
      const accountId = data.account?.id ?? data.user?.id ?? '';
      const resolvedEmail = data.account?.email ?? data.user?.email ?? email;
      setUnverifiedEmail(null);
      setAuth({ accessToken, accountId, email: resolvedEmail });
      void navigate('/profile');
    },
    [setAuth, navigate],
  );

  // 403 ACCOUNT_NOT_ACTIVATED → muro (não erro genérico). Outros códigos →
  // erro. `PASSWORD_CHANGE_REQUIRED` só chega aqui quando NÃO temos a senha
  // temporária: o caminho com senha o intercepta antes, em `signIn`.
  const handleError = useCallback(
    async (
      err: unknown,
      emailHint?: string,
      options?: { fromFederatedPopup?: boolean },
    ) => {
      const code = extractApiErrorCode(err);
      if (code === 'ACCOUNT_NOT_ACTIVATED') {
        setUnverifiedEmail(firebaseAuth.currentUser?.email ?? emailHint ?? '');
        return;
      }
      // O login no provedor já deixou uma sessão Firebase local e persistida; se
      // o backend recusou (e não é um dos casos acima, que seguem usando essa
      // sessão), ela é órfã — sem derrubá-la, o `onAuthStateChanged` da
      // `SignInPage` tentaria reconciliá-la sozinho no próximo carregamento,
      // repetindo o mesmo erro sem o usuário ter clicado em nada, e prendendo-o
      // na conta Google errada (google-sign-in §4.3, passo 5b). Só derruba
      // quando o backend de fato respondeu: queda de rede não é motivo.
      if (
        options?.fromFederatedPopup &&
        code === NO_ACCOUNT_CODE &&
        firebaseAuth.currentUser &&
        isFreshFederatedUid(firebaseAuth.currentUser)
      ) {
        try {
          await firebaseAuth.currentUser.delete();
        } catch {
          // Rede, ou sessão que o provedor não considera recente: a sessão ainda
          // é derrubada abaixo. Órfão a mais é higiene pendente, não bloqueio.
        }
      }
      // Esperado, não solto: as mensagens abaixo pedem uma nova entrada, e a
      // `SignInPage` entra sozinha se ainda achar sessão Firebase viva.
      if (isApiError(err) && !KEEPS_SESSION.has(code ?? '')) {
        await authContainer.repository.clearAuth();
      }
      if (code === 'ACCOUNT_NOT_FOUND') {
        setError(ACCOUNT_NOT_FOUND_MESSAGE);
        return;
      }
      // Chegar aqui significa não ter a temporária para reautenticar. Mandar
      // este caminho para a tela de troca criava um LAÇO: sem `tempPassword`
      // ela reencaminhava ao /sign-in, que entra sozinho ao ver sessão viva e
      // batia no mesmo 403. Derrubar a sessão (acima) corta a realimentação.
      if (code === 'PASSWORD_CHANGE_REQUIRED') {
        setError(PASSWORD_CHANGE_REQUIRED_MESSAGE);
        return;
      }
      setError(
        firebaseErrorMessage(err) ??
          (err instanceof Error ? err.message : 'Failed to sign in'),
      );
    },
    [],
  );

  const signIn = async (input: SignInInput) => {
    setIsLoading(true);
    setError(null);
    setAccountExists(false);
    setUnverifiedEmail(null);
    entryInFlight.current = true;
    try {
      const data = await authContainer.repository.signIn(input);
      handleSuccess(data, input.email);
    } catch (err) {
      // Caminho com senha: leva a temporária para a tela de troca (§3.4/§3.7),
      // que reautentica com ela e faz o re-login silencioso com a senha nova.
      if (extractApiErrorCode(err) === 'PASSWORD_CHANGE_REQUIRED') {
        void navigate('/change-password-required', {
          state: { email: input.email, tempPassword: input.password },
        });
        return;
      }
      await handleError(err, input.email);
    } finally {
      entryInFlight.current = false;
      setIsLoading(false);
    }
  };

  const signInWithToken = useCallback(
    async (firebaseToken: string) => {
      // Reconciliação de sessão preexistente. Se há uma entrada explícita em
      // curso, ela é quem manda — este caminho só existe para o carregamento de
      // página com sessão Firebase já gravada.
      if (entryInFlight.current) return;
      setIsLoading(true);
      setError(null);
      try {
        const data =
          await authContainer.repository.signInWithToken(firebaseToken);
        handleSuccess(data, '');
      } catch (err) {
        await handleError(err);
      } finally {
        setIsLoading(false);
      }
    },
    [handleSuccess, handleError],
  );

  const signInFederated = useCallback(
    async (method: FederatedMethod) => {
      setIsLoading(true);
      setPendingProvider(method);
      setError(null);
      setAccountExists(false);
      setUnverifiedEmail(null);
      entryInFlight.current = true;
      try {
        const data =
          method === 'google.com'
            ? await authContainer.repository.signInWithGoogle()
            : await authContainer.repository.signInWithMicrosoft();
        handleSuccess(data, '');
      } catch (err) {
        const code = firebaseErrorCode(err);
        // Desistência do popup: volta o formulário ao normal, sem mensagem.
        if (CANCELLED_POPUP_CODES.has(code)) return;
        // Só a Microsoft chega aqui: o Google o Firebase anexa ao uid que já
        // existe (verificado em `primum-core-dev`, 2026-09-03).
        if (code === ACCOUNT_EXISTS_CODE) {
          setAccountExists(true);
          return;
        }
        await handleError(err, undefined, { fromFederatedPopup: true });
      } finally {
        entryInFlight.current = false;
        setPendingProvider(null);
        setIsLoading(false);
      }
    },
    [handleSuccess, handleError],
  );

  const signInWithGoogle = useCallback(
    () => signInFederated('google.com'),
    [signInFederated],
  );

  const signInWithMicrosoft = useCallback(
    () => signInFederated('microsoft.com'),
    [signInFederated],
  );

  // "Já confirmei, continuar": re-tenta o `/auth/sign-in` com a sessão Firebase
  // que já existe (o login no Firebase deu certo; faltava só o `verifiedAt`).
  const retrySignIn = useCallback(async () => {
    const user = firebaseAuth.currentUser;
    if (!user) {
      setUnverifiedEmail(null);
      setError('Sessão expirada. Entre novamente.');
      return;
    }
    const token = await user.getIdToken();
    await signInWithToken(token);
  }, [signInWithToken]);

  // "Voltar": sai do muro e encerra a sessão Firebase pendurada (§5.1) — senão
  // o AuthGuard poderia liberar o app com a conta ainda não verificada.
  const exitVerification = useCallback(() => {
    setUnverifiedEmail(null);
    setError(null);
    void authContainer.repository.clearAuth();
  }, []);

  return {
    signIn,
    signInWithToken,
    signInWithGoogle,
    signInWithMicrosoft,
    retrySignIn,
    exitVerification,
    isLoading,
    pendingProvider,
    error,
    accountExists,
    unverifiedEmail,
  };
}
